import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Product } from './types/product';
import { getProducts } from './lib/product-database';
import { UserRole, ROLE_METADATA } from '@/components/authnz/types/rbac';
import { Loader2, CheckCircle2, XCircle, AlertCircle, Users } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';

interface EmailValidationResult {
  valid: string[];
  invalid: string[];
}
import logger from '@/lib/logging';
import { createSubscriptionApi } from '@/lib/subscriptions-admin-api';
import { useAdmin } from '@/contexts/AdminContext';
import { productsAssignableByActor } from '@/lib/practitioner-subscription-policy';

interface ProcessingResult {
  success: number;
  failed: number;
  errors: Array<{ email: string; error: string }>;
}

export default function BulkSubscriptionAssignment() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [emailText, setEmailText] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<UserRole>(UserRole.STUDENT);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isLifetime, setIsLifetime] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [validationResult, setValidationResult] = useState<EmailValidationResult | null>(null);
  const [processingResult, setProcessingResult] = useState<ProcessingResult | null>(null);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();
  const { user } = useAuth();
  const { isAuthorizedAdmin } = useAdmin();

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const productsData = await getProducts();
        const visibleProducts = productsAssignableByActor(productsData, isAuthorizedAdmin);
        setProducts(visibleProducts);
        if (visibleProducts.length > 0) {
          setSelectedProductId(visibleProducts[0].id);
          setSelectedRole(visibleProducts[0].userRole);
          setIsLifetime(visibleProducts[0].isLifetime);
        }
      } catch (error) {
        logger.error('Error loading products:', error);
        toast({
          title: 'Error',
          description: 'Failed to load products',
          variant: 'destructive',
        });
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
  }, [toast, isAuthorizedAdmin]);

  // Update role and lifetime when product changes
  useEffect(() => {
    if (selectedProductId) {
      const product = products.find(p => p.id === selectedProductId);
      if (product) {
        setSelectedRole(product.userRole);
        setIsLifetime(product.isLifetime);
      }
    }
  }, [selectedProductId, products]);

  // Validate emails whenever email text changes
  useEffect(() => {
    if (emailText.trim()) {
      const result = parseAndValidateEmails(emailText);
      setValidationResult(result);
    } else {
      setValidationResult(null);
    }
  }, [emailText]);

  /**
   * Parse emails from text input
   * Supports: newlines, commas, semicolons, spaces
   */
  function parseAndValidateEmails(text: string): EmailValidationResult {
    const valid: string[] = [];
    const invalid: string[] = [];

    // Split by newlines, commas, semicolons, or spaces
    const rawEmails = text
      .split(/[\n,;\s]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    rawEmails.forEach(email => {
      if (emailRegex.test(email)) {
        // Check for duplicates
        if (!valid.includes(email.toLowerCase())) {
          valid.push(email.toLowerCase());
        }
      } else {
        invalid.push(email);
      }
    });

    return { valid, invalid };
  }

  /**
   * Process subscriptions in batches
   */
  async function processBulkAssignment() {
    if (!validationResult || validationResult.valid.length === 0) {
      toast({
        title: 'No Valid Emails',
        description: 'Please enter at least one valid email address',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedProductId) {
      toast({
        title: 'Product Required',
        description: 'Please select a product',
        variant: 'destructive',
      });
      return;
    }

    if (!user?.uid) {
      toast({
        title: 'Authentication Required',
        description: 'You must be logged in to create subscriptions',
        variant: 'destructive',
      });
      return;
    }

    setProcessing(true);
    setProcessingResult(null);
    setProgress(0);

    const validEmails = validationResult.valid;
    const batchSize = 10; // Process 10 subscriptions at a time
    const totalBatches = Math.ceil(validEmails.length / batchSize);
    const results: ProcessingResult = {
      success: 0,
      failed: 0,
      errors: [],
    };

    try {
      for (let i = 0; i < totalBatches; i++) {
        const batch = validEmails.slice(i * batchSize, (i + 1) * batchSize);
        
        // Process batch in parallel
        // Create one subscription per email (each user gets their own subscription record)
        const batchResults = await Promise.allSettled(
          batch.map(async (email) => {
            try {
              const subscriptionData = {
                productId: selectedProductId,
                userId: user.uid, // Use admin's userId as the subscription owner/creator
                userEmails: [email], // One email per subscription for bulk assignment
                userRole: selectedRole,
                startDate: startDate ? new Date(startDate) : new Date(),
                currentPeriodStart: startDate ? new Date(startDate) : new Date(),
                currentPeriodEnd: endDate && !isLifetime ? new Date(endDate) : null,
                status: 'active' as const,
                isLifetime: isLifetime,
              };

              await createSubscriptionApi(subscriptionData);

              return { email, success: true };
            } catch (error: any) {
              return {
                email,
                success: false,
                error: error.message || 'Unknown error',
              };
            }
          })
        );

        // Process batch results
        batchResults.forEach((result) => {
          if (result.status === 'fulfilled') {
            if (result.value.success) {
              results.success++;
            } else {
              results.failed++;
              results.errors.push({
                email: result.value.email,
                error: result.value.error || 'Unknown error',
              });
            }
          } else {
            results.failed++;
            results.errors.push({
              email: 'unknown',
              error: result.reason?.message || 'Unknown error',
            });
          }
        });

        // Update progress
        setProgress(((i + 1) / totalBatches) * 100);
      }

      setProcessingResult(results);

      toast({
        title: 'Bulk Assignment Complete',
        description: `Successfully created ${results.success} subscription(s). ${results.failed > 0 ? `${results.failed} failed.` : ''}`,
        variant: results.failed > 0 ? 'default' : 'default',
      });

      // Clear form on success
      if (results.failed === 0) {
        setEmailText('');
        setValidationResult(null);
      }
    } catch (error: any) {
      logger.error('Error processing bulk assignment:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to process bulk assignment',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  }

  const selectedProduct = products.find(p => p.id === selectedProductId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-primary flex items-center gap-2">
          <Users className="h-5 w-5" />
          Bulk Subscription Assignment
        </CardTitle>
        <CardDescription>
          Paste a list of email addresses to create subscriptions for multiple users at once
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Email Input */}
        <div className="space-y-2">
          <Label htmlFor="emails">Email Addresses</Label>
          <Textarea
            id="emails"
            value={emailText}
            onChange={(e) => setEmailText(e.target.value)}
            placeholder="Paste email addresses here (one per line, or separated by commas, semicolons, or spaces)&#10;&#10;Example:&#10;user1@example.com&#10;user2@example.com&#10;user3@example.com"
            className="min-h-[300px] font-mono text-sm"
            disabled={processing}
          />
          <p className="text-xs text-muted-foreground">
            Supports multiple formats: one per line, comma-separated, semicolon-separated, or space-separated
          </p>
        </div>

        {/* Validation Results */}
        {validationResult && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4 text-success-text" />
                  <strong>{validationResult.valid.length}</strong> valid email(s)
                </span>
                {validationResult.invalid.length > 0 && (
                  <span className="flex items-center gap-1">
                    <XCircle className="h-4 w-4 text-error-text" />
                    <strong>{validationResult.invalid.length}</strong> invalid email(s)
                  </span>
                )}
              </div>
              {validationResult.invalid.length > 0 && (
                <div className="mt-2 text-xs">
                  <strong>Invalid emails:</strong> {validationResult.invalid.slice(0, 5).join(', ')}
                  {validationResult.invalid.length > 5 && ` and ${validationResult.invalid.length - 5} more`}
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Product Selection */}
        <div className="space-y-2">
          <Label htmlFor="product">Product</Label>
          <Select
            value={selectedProductId}
            onValueChange={setSelectedProductId}
            disabled={loadingProducts || processing}
          >
            <SelectTrigger id="product" className="h-12">
              <SelectValue placeholder="Select a product" />
            </SelectTrigger>
            <SelectContent>
              {products.map((product) => (
                <SelectItem key={product.id} value={product.id}>
                  {product.name} - {product.priceAmount ? `$${(product.priceAmount / 100).toFixed(2)}` : 'N/A'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedProduct && (
            <div className="p-3 bg-muted rounded-lg text-sm">
              <p className="font-medium mb-1">{selectedProduct.name}</p>
              <p className="text-muted-foreground text-xs mb-2">{selectedProduct.description}</p>
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs bg-background px-2 py-1 rounded">
                  Role: {ROLE_METADATA[selectedProduct.userRole].displayName}
                </span>
                {selectedProduct.isLifetime && (
                  <span className="text-xs bg-background px-2 py-1 rounded">Lifetime</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Role Display (derived from product) */}
        {selectedProduct && (
          <div className="space-y-2">
            <Label>Role to Grant</Label>
            <div className="p-3 bg-muted rounded-lg border">
              <div className="flex items-center gap-2">
                <span className="font-medium">{ROLE_METADATA[selectedRole].displayName}</span>
                <span className="text-xs text-muted-foreground">
                  (derived from product)
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="lifetime"
              checked={isLifetime}
              onChange={(e) => setIsLifetime(e.target.checked)}
              disabled={processing}
              className="h-4 w-4"
            />
            <Label htmlFor="lifetime" className="cursor-pointer">
              Lifetime Subscription (no end date)
            </Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={processing}
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date {isLifetime && '(disabled for lifetime)'}</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={processing || isLifetime}
                className="h-12"
                min={startDate || undefined}
              />
            </div>
          </div>
        </div>

        {/* Processing Progress */}
        {processing && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Processing subscriptions...</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} />
          </div>
        )}

        {/* Processing Results */}
        {processingResult && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4 text-success-text" />
                    <strong>{processingResult.success}</strong> successful
                  </span>
                  {processingResult.failed > 0 && (
                    <span className="flex items-center gap-1">
                      <XCircle className="h-4 w-4 text-error-text" />
                      <strong>{processingResult.failed}</strong> failed
                    </span>
                  )}
                </div>
                {processingResult.errors.length > 0 && (
                  <div className="mt-2 text-xs max-h-32 overflow-y-auto">
                    <strong>Errors:</strong>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      {processingResult.errors.slice(0, 10).map((error, idx) => (
                        <li key={idx}>
                          {error.email}: {error.error}
                        </li>
                      ))}
                      {processingResult.errors.length > 10 && (
                        <li>... and {processingResult.errors.length - 10} more errors</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Submit Button */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            onClick={processBulkAssignment}
            disabled={
              processing ||
              !validationResult ||
              validationResult.valid.length === 0 ||
              !selectedProductId
            }
            className="min-w-[200px]"
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Users className="h-4 w-4 mr-2" />
                Create {validationResult?.valid.length || 0} Subscription(s)
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

